import { useState } from 'react'
import { useAuth } from '../lib/AuthContext'
import { useLang } from '../lib/LangContext'
import { addProduit, enregistrerMouvementSansModif } from '../lib/api'
import { addProduitCaissier } from '../lib/apiCaissier'

const CATEGORIES = [
  { id: 'alimentation', label: '🍚\nAlimentation' },
  { id: 'boissons',     label: '🥤\nBoissons' },
  { id: 'hygiene',      label: '🧴\nHygiène' },
  { id: 'epices',       label: '🌶\nÉpices' },
  { id: 'conserves',    label: '🥫\nConserves' },
  { id: 'autres',       label: '📦\nAutres' },
]

export default function AjouterProduit({ onRefresh, boutiqueId: boutiqueIdProp }) {
  const { user, boutiqueId: boutiqueIdAuth } = useAuth()
  const { t, lang } = useLang()
  const isCaissier = !user && !!boutiqueIdProp
  const boutiqueId = boutiqueIdProp || boutiqueIdAuth

  const [form, setForm] = useState({
    nom: '', quantite: '', unite: '', prix_achat: '', prix_unitaire: '',
    seuil_alerte: '5', categorie: 'alimentation',
    est_perissable: false, date_peremption: ''
  })
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)

  function set(key, val) { setForm(f => ({ ...f, [key]: val })) }

  // Calcul marge temps réel
  const marge = form.prix_unitaire && form.prix_achat
    ? parseInt(form.prix_unitaire) - parseInt(form.prix_achat)
    : null

  // Calcul durée de vie depuis la date de péremption
  const dureeVieJours = form.date_peremption
    ? Math.ceil((new Date(form.date_peremption) - new Date()) / (1000 * 60 * 60 * 24))
    : null

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.nom.trim()) { setMsg({ type: 'error', text: t('name_required') }); return }
    if (form.est_perissable && !form.date_peremption) {
      setMsg({ type: 'error', text: lang === 'fr' ? 'Entre la date de péremption.' : 'Enter the expiry date.' })
      return
    }
    if (form.est_perissable && dureeVieJours !== null && dureeVieJours < 0) {
      setMsg({ type: 'error', text: lang === 'fr' ? 'La date de péremption est déjà dépassée !' : 'The expiry date is already past!' })
      return
    }
    setSaving(true); setMsg(null)

    const produitData = {
      nom: form.nom.trim(),
      categorie: form.categorie,
      quantite: parseFloat(form.quantite) || 0,
      unite: form.unite.trim() || 'unité',
      prix_achat: parseInt(form.prix_achat) || 0,
      prix_unitaire: parseInt(form.prix_unitaire) || 0,
      seuil_alerte: parseFloat(form.seuil_alerte) || 5,
      est_perissable: form.est_perissable,
      date_peremption: form.est_perissable && form.date_peremption
        ? new Date(form.date_peremption).toISOString() : null,
      duree_vie_jours: dureeVieJours
    }

    let produit, error

    if (isCaissier) {
      const res = await addProduitCaissier(boutiqueId, produitData)
      produit = res.data; error = res.error
    } else {
      const res = await addProduit(boutiqueId, produitData)
      produit = res.data; error = res.error
      if (!error && produitData.quantite > 0) {
        await enregistrerMouvementSansModif(boutiqueId, {
          produitId: produit.id, produitNom: produit.nom,
          type: 'entree', quantite: produitData.quantite, note: 'Stock initial',
          prixAchat: produitData.prix_achat || 0,
          prixUnitaire: produitData.prix_unitaire || 0
        })
      }
    }

    if (error) { setMsg({ type: 'error', text: error.message }); setSaving(false); return }

    setForm({
      nom: '', quantite: '', unite: '', prix_achat: '', prix_unitaire: '',
      seuil_alerte: '5', categorie: 'alimentation',
      est_perissable: false, date_peremption: ''
    })
    setMsg({ type: 'success', text: `✓ "${produit.nom}" ${t('product_added')}` })
    setSaving(false)
    onRefresh()
  }

  const today = new Date().toISOString().split('T')[0]

  return (
    <div className="main">
      <div className="page-title">{t('new_product')}</div>
      <form onSubmit={handleSubmit}>

        {/* NOM */}
        <div className="field">
          <label>{t('product_name')}</label>
          <input
            type="text"
            placeholder={t('product_name_placeholder')}
            value={form.nom}
            onChange={e => set('nom', e.target.value)}
          />
        </div>

        {/* QUANTITÉ + UNITÉ */}
        <div className="form-row">
          <div className="field">
            <label>{t('initial_qty')}</label>
            <input
              type="number"
              placeholder="0"
              min="0"
              step="0.5"
              value={form.quantite}
              onChange={e => set('quantite', e.target.value)}
            />
          </div>
          <div className="field">
            <label>{t('unit')}</label>
            <input
              type="text"
              placeholder={t('unit_placeholder')}
              value={form.unite}
              onChange={e => set('unite', e.target.value)}
            />
          </div>
        </div>

        {/* PRIX */}
        <div style={{
          background: 'var(--sage-pale)', border: '1px solid var(--sage-light)',
          borderRadius: 'var(--radius-lg)', padding: '1rem', marginBottom: '1rem'
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--sage-dark)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            💰 {lang === 'fr' ? 'Prix (important pour le bilan)' : 'Prices (important for reports)'}
          </div>
          <div className="form-row">
            <div className="field">
              <label style={{ color: 'var(--rose)' }}>
                🛒 {lang === 'fr' ? "Prix d'achat (fournisseur)" : 'Purchase price (supplier)'}
              </label>
              <input
                type="number"
                placeholder="0"
                min="0"
                value={form.prix_achat}
                onChange={e => set('prix_achat', e.target.value)}
              />
            </div>
            <div className="field">
              <label style={{ color: 'var(--sage)' }}>
                💵 {lang === 'fr' ? 'Prix de vente (client)' : 'Selling price (customer)'}
              </label>
              <input
                type="number"
                placeholder="0"
                min="0"
                value={form.prix_unitaire}
                onChange={e => set('prix_unitaire', e.target.value)}
              />
            </div>
          </div>

          {/* MARGE TEMPS RÉEL */}
          {marge !== null && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: marge > 0 ? 'var(--warm-white)' : 'var(--rose-pale)',
              borderRadius: 'var(--radius-md)', padding: '8px 12px', marginTop: 4
            }}>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                {lang === 'fr' ? 'Bénéfice par unité' : 'Profit per unit'}
              </div>
              <div style={{ fontFamily: 'Fraunces, serif', fontSize: 16, color: marge > 0 ? 'var(--sage-dark)' : 'var(--rose)' }}>
                {marge > 0 ? '+' : ''}{marge.toLocaleString()} FCFA
                {form.prix_unitaire > 0 && (
                  <span style={{ fontSize: 11, marginLeft: 6, color: 'var(--muted)' }}>
                    ({Math.round((marge / parseInt(form.prix_unitaire)) * 100)}%)
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* SEUIL D'ALERTE */}
        <div className="form-row">
          <div className="field">
            <label>{t('alert_threshold')}</label>
            <input
              type="number"
              placeholder="5"
              min="0"
              step="0.5"
              value={form.seuil_alerte}
              onChange={e => set('seuil_alerte', e.target.value)}
            />
          </div>
        </div>

        {/* PRODUIT PÉRISSABLE */}
        <div style={{
          background: form.est_perissable ? 'var(--amber-pale)' : 'var(--warm-white)',
          border: `1.5px solid ${form.est_perissable ? '#E8C080' : 'var(--cream-dark)'}`,
          borderRadius: 'var(--radius-lg)', padding: '1rem', marginBottom: '1rem',
          transition: 'all 0.2s'
        }}>
          {/* TOGGLE */}
          <button
            type="button"
            onClick={() => set('est_perissable', !form.est_perissable)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              fontFamily: 'DM Sans, sans-serif'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 20 }}>⏰</span>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--charcoal)' }}>
                  {lang === 'fr' ? 'Produit périssable ?' : 'Perishable product?'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                  {lang === 'fr' ? 'Yaourt, pain, fruits, légumes...' : 'Yogurt, bread, fruits, vegetables...'}
                </div>
              </div>
            </div>
            {/* Switch */}
            <div style={{
              width: 46, height: 26, borderRadius: 13, padding: 3,
              background: form.est_perissable ? 'var(--amber)' : 'var(--cream-dark)',
              display: 'flex', alignItems: 'center',
              justifyContent: form.est_perissable ? 'flex-end' : 'flex-start',
              transition: 'all 0.2s', flexShrink: 0
            }}>
              <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
            </div>
          </button>

          {/* DATE PÉREMPTION */}
          {form.est_perissable && (
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #E8C080' }}>
              <div className="field" style={{ margin: 0 }}>
                <label style={{ color: '#854F0B' }}>
                  📅 {lang === 'fr' ? 'Date de péremption *' : 'Expiry date *'}
                </label>
                <input
                  type="date"
                  min={today}
                  value={form.date_peremption}
                  onChange={e => set('date_peremption', e.target.value)}
                  style={{ borderColor: '#E8C080' }}
                />
              </div>

              {/* DURÉE DE VIE CALCULÉE */}
              {dureeVieJours !== null && (
                <div style={{
                  marginTop: 8, padding: '8px 12px', borderRadius: 'var(--radius-md)',
                  background: dureeVieJours < 0 ? 'var(--rose-pale)' : dureeVieJours < 7 ? 'var(--rose-pale)' : dureeVieJours < 30 ? '#FDF3E3' : 'var(--sage-pale)',
                  display: 'flex', alignItems: 'center', gap: 8
                }}>
                  <span style={{ fontSize: 16 }}>
                    {dureeVieJours < 0 ? '❌' : dureeVieJours < 7 ? '🚨' : dureeVieJours < 30 ? '⚠️' : '✅'}
                  </span>
                  <div style={{ fontSize: 12, color: 'var(--charcoal)' }}>
                    {dureeVieJours < 0
                      ? (lang === 'fr' ? 'Date déjà dépassée !' : 'Date already past!')
                      : lang === 'fr'
                        ? `Ce produit a encore ${dureeVieJours} jour(s) de durée de vie`
                        : `This product has ${dureeVieJours} day(s) of shelf life left`}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* CATÉGORIE */}
        <div className="field">
          <label>{t('category')}</label>
          <div className="cat-grid">
            {CATEGORIES.map(c => (
              <button
                key={c.id}
                type="button"
                className={`cat-btn ${form.categorie === c.id ? 'selected' : ''}`}
                onClick={() => set('categorie', c.id)}
                style={{ whiteSpace: 'pre-line' }}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <button
          type="submit"
          className="btn-primary"
          style={{ marginTop: '1.25rem' }}
          disabled={saving}
        >
          {saving ? t('saving') : t('add_to_stock')}
        </button>
      </form>

      {msg && (
        <div
          className={msg.type === 'success' ? 'success-msg' : 'error-msg'}
          style={{ marginTop: '0.75rem' }}
        >
          {msg.text}
        </div>
      )}
    </div>
  )
}
